import React, { useEffect, useState } from 'react';

import { Carousel, CarouselProps, Embla } from '@mantine/carousel';
import { Box, useMantineTheme } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconArrowLeft, IconArrowRight, IconX } from '@tabler/icons-react';
import { ChatConversationView } from 'components/SmartAssistantComponents/ChatConversationView';
import { ChatHeader } from 'components/SmartAssistantComponents/ChatHeader';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ManageSmartAssistantTabSlug } from 'shared/constants/route-links';
import { SmartAssistantVisualConfig, WebsiteConfigurationFormValues } from 'shared/interfaces/SmartAssistant';

interface WebsiteIntegrationPreviewCarouselProps extends CarouselProps {
  smartAssistantVisualConfig: SmartAssistantVisualConfig;
  websiteIntegrationForm: UseFormReturnType<WebsiteConfigurationFormValues>;
}

export function WebsiteIntegrationPreviewCarousel({
  smartAssistantVisualConfig,
  websiteIntegrationForm,
  ...carouselProps
}: WebsiteIntegrationPreviewCarouselProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const location = useLocation();
  const [subscription] = useSubscriptionContext();
  const [embla, setEmbla] = useState<Embla | null>(null);

  const isWebsiteIntegrationTabActive = location.pathname.includes(ManageSmartAssistantTabSlug.WEBSITE_INTEGRATION);

  // Reinit when this tab becomes active to show the correct preview
  useEffect(() => {
    if (embla && isWebsiteIntegrationTabActive) {
      embla.reInit();
    }
  }, [embla, isWebsiteIntegrationTabActive]);

  const userMessage = {
    text: t(`SMART_ASSISTANT.PREVIEW.USER_MESSAGE`),
    fromUser: true
  };

  const smartAssistantMessage = {
    text: t(`SMART_ASSISTANT.PREVIEW.SMART_ASSISTANT_MESSAGE`),
    fromUser: false
  };

  const smartAssistantWelcomeMessage = {
    text:
      smartAssistantVisualConfig.assistantWelcomeMessage ||
      t(`SMART_ASSISTANT.PREVIEW.SMART_ASSISTANT_WELCOME_MESSAGE`),
    fromUser: false
  };

  const PREVIEWS_DATA = [
    // Bubble with welcome header
    {
      messages: [smartAssistantWelcomeMessage],
      xIconColor: theme.other.getReadableColor(smartAssistantVisualConfig.conversationBackgroundColor)
    },
    // Bubble without welcome header
    {
      messages: [userMessage, smartAssistantMessage],
      xIconColor: theme.other.getReadableColor(smartAssistantVisualConfig.secondaryBackgroundColor)
    }
  ];

  return (
    <Carousel
      withIndicators
      w="100%"
      pb={30}
      getEmblaApi={setEmbla}
      nextControlIcon={<IconArrowRight style={{ width: 20, height: 20 }} />}
      previousControlIcon={<IconArrowLeft style={{ width: 20, height: 20 }} />}
      styles={(theme) => ({
        indicator: {
          backgroundColor: theme.colors.gray[6]
        }
      })}
      {...carouselProps}>
      {PREVIEWS_DATA.map((pd, index) => (
        <Carousel.Slide key={index}>
          <Box mx="auto" w="80%">
            <Box w={400} h={700} mx="auto" style={{ position: 'relative', borderRadius: 16, overflow: 'hidden' }}>
              <IconX
                color={pd.xIconColor}
                style={{ position: 'absolute', top: 20, right: 20, strokeWidth: 0.7, zIndex: 2 }}
              />
              <ChatConversationView
                messages={pd.messages}
                viewMode="preview"
                smartAssistantVisualConfig={smartAssistantVisualConfig}
                welcomeHeaderProps={{
                  title: websiteIntegrationForm.values.welcomeHeaderTitle,
                  subtitle: websiteIntegrationForm.values.welcomeHeaderSubtitle,
                  avatar: smartAssistantVisualConfig.assistantAvatar
                }}
                header={<ChatHeader smartAssistantVisualConfig={smartAssistantVisualConfig} justify="flex-start" />}
                displayPoweredBy={!subscription.product.removePoweredBy}
              />
            </Box>
          </Box>
        </Carousel.Slide>
      ))}
    </Carousel>
  );
}
